import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';
import {
  computeDaysOfSupply,
  computeParSuggestion,
  computeProductStatus,
  DOS_AMBER_THRESHOLD_DAYS,
  formatBurnRate,
  isTrackingMode,
  type CategoryRow,
  type ProductRow,
  type ProductStatus,
  type TrackingMode,
} from '@/lib/inventory';
import { useInventoryUiStore } from '@/lib/inventoryUiStore';

// ============================================================
// InventoryPage — Phase 2, Epic 2A.1, with 2A.2 + 2A.3 + 2A.5 layered in
// 4A.1 (2026-05-16): burn-rate fetch swapped to product_burn_rates view.
// 4A.3a (2026-05-19): par editor integrated. See DECISIONS 2026-05-19.
// ============================================================
// First page that reads tenant data through application code.
// Up to this point RLS has been exercised by verification SQL;
// 2A.1 puts it on the application read path. Categories and
// products are fetched in parallel, both filtered by the
// active membership's clinic_id.
//
// 2A.2 lifts category accordion state from local React state
// into a persisted Zustand store (`useInventoryUiStore`) so
// expand/collapse survives reload and PWA refresh.
//
// 2A.3 adds the +/- count stepper for count-tracked categories
// (A/B). First WRITE on tenant data: each tap inserts an
// `inventory_events` row with `kind = 'manual_adjustment'` and
// the BEFORE INSERT trigger atomically updates `products.count`.
// Updates are optimistic — the local count moves on tap and is
// rolled back if the insert fails. Per-row inline error shows
// briefly when an insert fails (proper toast system lands in
// the next ticket; inline is per-row context that's still
// useful even after toasts arrive).
//
// 2A.5 adds burn-rate (~X/mo) and days-of-supply labels on
// each row. 4A.1 swapped the on-the-fly aggregation for a
// direct read of `product_burn_rates` — same downstream shape.
//
// 4A.3a adds the par editor: on rows where the caller holds
// `par.edit` and the product has a non-null par, the "par N"
// label in the meta line becomes a tap target that expands an
// inline stepper + optional reason + Save/Cancel below the
// row. Save invokes `update_product_par` RPC; the BEFORE
// trigger applies the par mutation, the AFTER trigger
// evaluates enqueue. Optimistic local update with rollback on
// error, same pattern as count adjust. See DECISIONS 2026-05-19
// for the seven-fork ratification.
//
// Out of scope (deferred):
//   - Toast / global error surface (next ticket)
//   - Kanban "Log Usage" button on inventory page (build plan
//     scopes 2A.3 to A/B; kanban interactivity not yet ticketed)
//   - 4A.3 par auto-suggestion hint ("Bump par 4 → 6 based on
//     burn rate") — routes into the same stepper component when
//     it lands. No editor-side changes required by that ticket.
//
// Default expansion: all categories expanded so the page is
// immediately useful without first interaction. The default
// lives at the read site (`expanded[id] ?? true`) — the store
// only records explicit user toggles, so new categories from
// onboarding don't need a store update.
//
// Product ordering: essentials first, then alphabetical. Stable
// sort within those buckets — clinical priority floats up while
// the rest stays predictable for inventory work.
//
// RLS: `inventory_events` has both select and insert policies
// gated on `is_clinic_member(clinic_id)`. The trigger is
// SECURITY DEFINER so it can update `products` even though
// staff are read-only on the products table. `actor_id` is set
// application-side from the active session; trust is
// application-level for now (a clinic member could in theory
// spoof another member's actor_id by hand-crafting a request)
// — pinning at the DB level via an `actor_id default auth.uid()`
// and an RLS check is a known follow-up. See the 2A.3 entry
// in DECISIONS.md.
//
// Par editor authZ: routed through `update_product_par` RPC
// which gates on `has_permission(par.edit)` server-side. The
// PWA `hasPermission(session, 'par.edit')` check here is for
// UI gating only — server is the actual gate. See DECISIONS
// 2026-05-19 implications section ("Why the RPC despite
// manager+ products RLS").
// ============================================================

type Category = Omit<CategoryRow, 'tracking_mode'> & {
  tracking_mode: TrackingMode;
};

type FetchedData = {
  categories: Category[];
  products: ProductRow[];
  /**
   * Per-product monthly burn rate (units/month) read from
   * the `product_burn_rates` view. Missing keys mean the
   * product has no qualifying consumption in the window —
   * the row hides the burn / DoS labels.
   *
   * Replaces the prior 2A.5 runtime aggregation from
   * `inventory_events`; see DECISIONS 2026-05-16 for the
   * 4A.1 mat-view swap.
   */
  burnByProduct: Map<string, number>;
  /**
   * Per-product 4-week qualifying consumption count from the
   * same `product_burn_rates` view. Used as the confidence
   * floor for the par auto-suggestion (4A.3). Missing key →
   * no data → no suggestion.
   */
  units4wByProduct: Map<string, number>;
  /**
   * Per-product dismissal record for the current user, keyed
   * on product_id → dismissed_for_par. The suggestion is
   * suppressed only while the product's current par equals
   * `dismissed_for_par` (par-value invalidation, Fork 2 D).
   */
  dismissedByProduct: Map<string, number>;
};

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; data: FetchedData };

type PillStatus = Exclude<ProductStatus, 'dead'>;

const PILL_META: Record<
  PillStatus,
  { label: string; bg: string; text: string; dot: string; border: string }
> = {
  // Update #9 §1 (2026-07-17): stoplight saturated primaries replaced
  // by the muted status trio — forest (stocked), gold (low), rust
  // (critical). Tint backgrounds are the same hue at low opacity per
  // spec, not separate lighter colors. `border` powers the stat-tile
  // treatment (tinted bg + border in the status color).
  stocked: {
    label: 'Stocked',
    bg: 'bg-forest/[.08]',
    text: 'text-forest',
    dot: 'bg-forest',
    border: 'border-forest/30',
  },
  low: {
    label: 'Low',
    bg: 'bg-gold/[.08]',
    text: 'text-gold',
    dot: 'bg-gold',
    border: 'border-gold/30',
  },
  critical: {
    label: 'Critical',
    bg: 'bg-rust/[.08]',
    text: 'text-rust',
    dot: 'bg-rust',
    border: 'border-rust/30',
  },
};

const STATUS_DOT: Record<ProductStatus, string> = {
  stocked: 'bg-forest',
  low: 'bg-gold',
  critical: 'bg-rust',
  dead: 'bg-slate-300',
};

const CATEGORY_BADGE: Record<string, string> = {
  A: 'bg-blue-100 text-blue-700',
  B: 'bg-violet-100 text-violet-700',
  C: 'bg-amber-100 text-amber-700',
  D: 'bg-slate-100 text-slate-700',
};

const PILL_KEYS: readonly PillStatus[] = ['stocked', 'low', 'critical'];

const EMPTY_TOTALS: Record<PillStatus, number> = {
  stocked: 0,
  low: 0,
  critical: 0,
};

function compareProducts(a: ProductRow, b: ProductRow): number {
  if (a.essential !== b.essential) return a.essential ? -1 : 1;
  return a.name.localeCompare(b.name);
}

export default function InventoryPage() {
  const session = useSession();
  const { activeMembership, userId } = session;
  const clinicId = activeMembership.clinic_id;

  // 4A.3a: PWA-side gate for showing the par editor affordance.
  // The actual gate is the server-side `has_permission(par.edit)`
  // check inside the `update_product_par` RPC. This is purely a
  // UI affordance — don't render a tap target the caller can't
  // act on. Reads against the effective permission set, so a
  // manager-real / staff-viewAs session correctly hides the
  // affordance.
  const canEditPar = hasPermission(session, 'par.edit');

  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [retryToken, setRetryToken] = useState(0);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    async function load(): Promise<void> {
      const [catsResult, prodsResult, burnResult, dismissalsResult] = await Promise.all([
        supabase
          .from('categories')
          .select('*')
          .eq('clinic_id', clinicId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('products')
          .select('*')
          .eq('clinic_id', clinicId),
        // 4A.1: read from the rolling-burn-rate mat view rather
        // than computing client-side. The view's burn_rate_4w is
        // computed at the nightly refresh wall-clock anchor, so
        // intraday writes (scans, receipts) won't move it until
        // tomorrow's tick — acceptable for the "trend" semantic
        // we want here.
        //
        // 4A.3: also select units_4w (already exposed by the
        // wrapper view) for the par-suggestion confidence floor.
        supabase
          .from('product_burn_rates')
          .select('product_id, burn_rate_4w, units_4w')
          .eq('clinic_id', clinicId),
        // 4A.3: per-user dismissal records for the par-bump
        // suggestion. RLS scopes the read to the calling user;
        // the clinic_id filter is belt-and-suspenders + planner
        // hint for the (clinic_id) index.
        supabase
          .from('par_suggestion_dismissals')
          .select('product_id, dismissed_for_par')
          .eq('user_id', userId)
          .eq('clinic_id', clinicId),
      ]);

      if (cancelled) return;

      if (catsResult.error) {
        console.error(
          '[viashift] Inventory categories fetch failed:',
          catsResult.error,
        );
        setState({ kind: 'error', message: catsResult.error.message });
        return;
      }
      if (prodsResult.error) {
        console.error(
          '[viashift] Inventory products fetch failed:',
          prodsResult.error,
        );
        setState({ kind: 'error', message: prodsResult.error.message });
        return;
      }
      if (burnResult.error) {
        console.error(
          '[viashift] Inventory burn rates fetch failed:',
          burnResult.error,
        );
        setState({ kind: 'error', message: burnResult.error.message });
        return;
      }
      if (dismissalsResult.error) {
        console.error(
          '[viashift] Inventory par-suggestion dismissals fetch failed:',
          dismissalsResult.error,
        );
        setState({ kind: 'error', message: dismissalsResult.error.message });
        return;
      }

      // Narrow tracking_mode at the query boundary. Rows that
      // fail narrowing are logged and dropped — should never
      // happen in practice (CHECK constraint pins the values).
      const categories: Category[] = [];
      for (const row of catsResult.data ?? []) {
        if (!isTrackingMode(row.tracking_mode)) {
          console.error(
            '[viashift] Unexpected tracking_mode value, skipping row:',
            row,
          );
          continue;
        }
        categories.push({ ...row, tracking_mode: row.tracking_mode });
      }

      // Build the burn-rate map and the 4-week units map from the
      // same view rows. Two maps rather than one struct keeps the
      // downstream consumer (CountRow burn label) untouched —
      // it still reads a number directly off the map. units4w
      // is consumed only by the par-suggestion code path.
      //
      // Products with null burn_rate_4w (no qualifying consumption)
      // are absent from burnByProduct but may still be in
      // units4wByProduct if units_4w is zero — both representations
      // exist; the par-suggestion gate handles either.
      const burnByProduct = new Map<string, number>();
      const units4wByProduct = new Map<string, number>();
      for (const row of burnResult.data ?? []) {
        // product_id is non-null in practice (the underlying mat
        // view joins from products.id, a primary key) but the
        // view's column type can't propagate NOT NULL through
        // PostgREST, so the regen types it `string | null`.
        // Belt-and-suspenders narrow before .set().
        if (row.product_id === null) continue;
        if (row.burn_rate_4w !== null) {
          burnByProduct.set(row.product_id, row.burn_rate_4w);
        }
        if (row.units_4w !== null) {
          units4wByProduct.set(row.product_id, row.units_4w);
        }
      }

      // Build the per-product dismissal map for this user. PK is
      // (user_id, product_id) so at most one row per product —
      // the map is the natural shape.
      const dismissedByProduct = new Map<string, number>();
      for (const row of dismissalsResult.data ?? []) {
        if (row.product_id === null) continue;
        dismissedByProduct.set(row.product_id, row.dismissed_for_par);
      }

      setState({
        kind: 'loaded',
        data: {
          categories,
          products: prodsResult.data ?? [],
          burnByProduct,
          units4wByProduct,
          dismissedByProduct,
        },
      });
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [clinicId, retryToken, userId]);

  // Auto-clear inline row errors after a brief display window.
  // A new error during the window resets the timer (effect
  // cleanup cancels the prior timeout, fresh timeout starts).
  // Errors clear together rather than per-product because
  // multiple simultaneous failures clearing in a staggered
  // fashion looks twitchy.
  useEffect(() => {
    if (Object.keys(rowErrors).length === 0) return;
    const timeout = window.setTimeout(() => setRowErrors({}), 4000);
    return () => window.clearTimeout(timeout);
  }, [rowErrors]);

  const onRetry = useCallback(() => setRetryToken((t) => t + 1), []);

  // Optimistic count adjustment.
  //
  // 1. Bump the local product count by `delta` immediately.
  //    Tap feels physical-button-instant.
  // 2. Clear any prior error for this row (we're optimistically
  //    assuming this attempt succeeds).
  // 3. Insert the inventory_events row. The BEFORE INSERT
  //    trigger atomically updates `products.count` and stamps
  //    `count_after` from the post-update value, so the local
  //    optimistic count and the authoritative DB count converge
  //    on the next page-level refetch.
  // 4. On failure, roll back the local change by the inverse
  //    delta and surface a per-row inline error message. Best-
  //    effort rollback — concurrent taps may slightly drift
  //    until the next refetch reconciles.
  //
  // `count_after: 0` is a placeholder. The trigger overwrites
  // it before the row is committed. Sending 0 (rather than the
  // optimistic post-tap count) means that if the trigger ever
  // gets disabled, every event will land with count_after=0 —
  // loudly broken — instead of plausible-looking-but-wrong
  // values. Loud > silent.
  const adjustCount = useCallback(
    async (productId: string, delta: number): Promise<void> => {
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          data: {
            ...prev.data,
            products: prev.data.products.map((p) =>
              p.id === productId ? { ...p, count: p.count + delta } : p,
            ),
          },
        };
      });

      setRowErrors((prev) => {
        if (!(productId in prev)) return prev;
        const next = { ...prev };
        delete next[productId];
        return next;
      });

      const { error } = await supabase.from('inventory_events').insert({
        clinic_id: clinicId,
        product_id: productId,
        kind: 'manual_adjustment',
        delta,
        count_after: 0,
        actor_id: userId,
      });

      if (error) {
        console.error('[viashift] Manual adjustment failed:', error);
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            data: {
              ...prev.data,
              products: prev.data.products.map((p) =>
                p.id === productId ? { ...p, count: p.count - delta } : p,
              ),
            },
          };
        });
        setRowErrors((prev) => ({
          ...prev,
          [productId]: "Couldn't save — try again",
        }));
      }
    },
    [clinicId, userId],
  );

  // 4A.3a: Optimistic par mutation via the update_product_par
  // RPC. Caller (CountRow's editor) passes the new par and the
  // old par; the old par is used for local rollback if the RPC
  // fails. Reason is optional; when present it's appended to
  // the `notes` field of the generated par_changed event.
  //
  // RPC handles authZ (`has_permission(par.edit)`) server-side
  // — the UI gate (`canEditPar`) is only for hiding the
  // affordance. A spoofed call from a viewAs=staff session
  // still gets denied at the RPC.
  //
  // Errors:
  //   - 42501  permission denied (shouldn't reach here under
  //            normal flow because we gate the UI, but surfaces
  //            cleanly if a tab was open across a permission
  //            change)
  //   - 22023  validation (negative par, kanban product)
  //   - P0002  product not found (rare; means stale UI state)
  //   - else   network / unexpected
  //
  // Throws on error so the row-level editor can keep its open
  // state (user retries or cancels). Page-level rowErrors gets
  // the message regardless.
  const setPar = useCallback(
    async (
      productId: string,
      oldPar: number,
      newPar: number,
      reason: string,
    ): Promise<void> => {
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          data: {
            ...prev.data,
            products: prev.data.products.map((p) =>
              p.id === productId ? { ...p, par: newPar } : p,
            ),
          },
        };
      });

      setRowErrors((prev) => {
        if (!(productId in prev)) return prev;
        const next = { ...prev };
        delete next[productId];
        return next;
      });

      // p_reason uses conditional-spread rather than explicit
      // undefined/null because tsconfig has
      // exactOptionalPropertyTypes: true — the RPC's optional
      // arg signature `p_reason?: string` doesn't accept
      // `string | undefined`, only present-with-string or
      // genuinely absent. The conditional spread produces both
      // shapes correctly; the RPC's `text default null` then
      // handles the absent case.
      const reasonTrimmed = reason.trim();
      const { error } = await supabase.rpc('update_product_par', {
        p_product_id: productId,
        p_clinic_id: clinicId,
        p_new_par: newPar,
        ...(reasonTrimmed ? { p_reason: reasonTrimmed } : {}),
      });

      if (error) {
        console.error('[viashift] Set par failed:', error);
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            data: {
              ...prev.data,
              products: prev.data.products.map((p) =>
                p.id === productId ? { ...p, par: oldPar } : p,
              ),
            },
          };
        });
        const msg =
          error.code === '42501'
            ? "You don't have permission to edit par"
            : error.code === '22023'
              ? error.message.includes('kanban')
                ? "Par editor doesn't apply to kanban products"
                : 'Invalid par value'
              : "Couldn't save par — try again";
        setRowErrors((prev) => ({ ...prev, [productId]: msg }));
        throw error;
      }
    },
    [clinicId],
  );

  // 4A.3: Optimistic dismissal upsert. Updates the local
  // dismissedByProduct map so the suggestion disappears
  // immediately; rolls back on error. Direct table write under
  // RLS — no RPC. Upsert key is (user_id, product_id), so
  // re-dismissing on the same product just overwrites the prior
  // dismissed_for_par.
  //
  // Errors are quiet (no row-level error surface) because the
  // dismiss action is non-critical — if it fails, the
  // suggestion just stays visible and the user can try again.
  // Logged for diagnosis.
  const dismissSuggestion = useCallback(
    async (productId: string, dismissedForPar: number): Promise<void> => {
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        const nextMap = new Map(prev.data.dismissedByProduct);
        nextMap.set(productId, dismissedForPar);
        return {
          kind: 'loaded',
          data: { ...prev.data, dismissedByProduct: nextMap },
        };
      });

      const { error } = await supabase
        .from('par_suggestion_dismissals')
        .upsert(
          {
            user_id: userId,
            product_id: productId,
            clinic_id: clinicId,
            dismissed_for_par: dismissedForPar,
          },
          { onConflict: 'user_id,product_id' },
        );

      if (error) {
        console.error(
          '[viashift] Dismiss par suggestion failed:',
          error,
        );
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          const nextMap = new Map(prev.data.dismissedByProduct);
          nextMap.delete(productId);
          return {
            kind: 'loaded',
            data: { ...prev.data, dismissedByProduct: nextMap },
          };
        });
      }
    },
    [clinicId, userId],
  );

  if (state.kind === 'loading') {
    return (
      <InventoryShell totals={EMPTY_TOTALS}>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </InventoryShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <InventoryShell totals={EMPTY_TOTALS}>
        <ErrorCard message={state.message} onRetry={onRetry} />
      </InventoryShell>
    );
  }

  return (
    <InventoryLoaded
      data={state.data}
      adjustCount={adjustCount}
      setPar={setPar}
      dismissSuggestion={dismissSuggestion}
      canEditPar={canEditPar}
      rowErrors={rowErrors}
    />
  );
}

// ---- Loaded body ---------------------------------------------------

interface LoadedProps {
  data: FetchedData;
  adjustCount: (productId: string, delta: number) => Promise<void>;
  setPar: (
    productId: string,
    oldPar: number,
    newPar: number,
    reason: string,
  ) => Promise<void>;
  dismissSuggestion: (
    productId: string,
    dismissedForPar: number,
  ) => Promise<void>;
  canEditPar: boolean;
  rowErrors: Record<string, string>;
}

function InventoryLoaded({
  data,
  adjustCount,
  setPar,
  dismissSuggestion,
  canEditPar,
  rowErrors,
}: LoadedProps) {
  // Subscribe to the persisted UI store. Each toggle creates a
  // new `expanded` object reference, which re-renders this
  // component; React's reconciliation keeps the actual DOM
  // updates scoped to the one section that actually changed.
  const expanded = useInventoryUiStore((s) => s.expanded);
  const toggleCategory = useInventoryUiStore((s) => s.toggleCategory);

  const totals = useMemo(() => {
    const counts: Record<PillStatus, number> = { ...EMPTY_TOTALS };
    for (const p of data.products) {
      const s = computeProductStatus(p);
      if (s === 'dead') continue;
      counts[s]++;
    }
    return counts;
  }, [data.products]);

  const productsByCategory = useMemo(() => {
    const map = new Map<string, ProductRow[]>();
    for (const cat of data.categories) map.set(cat.id, []);
    for (const p of data.products) {
      const list = map.get(p.category_id);
      if (list) list.push(p);
    }
    for (const list of map.values()) list.sort(compareProducts);
    return map;
  }, [data.categories, data.products]);

  if (data.products.length === 0) {
    return (
      <InventoryShell totals={totals}>
        <EmptyState />
      </InventoryShell>
    );
  }

  return (
    <InventoryShell totals={totals}>
      {data.categories.map((cat) => {
        const products = productsByCategory.get(cat.id) ?? [];
        const isOpen = expanded[cat.id] ?? true;
        return (
          <CategorySection
            key={cat.id}
            category={cat}
            products={products}
            isOpen={isOpen}
            onToggle={() => toggleCategory(cat.id)}
            adjustCount={adjustCount}
            setPar={setPar}
            dismissSuggestion={dismissSuggestion}
            canEditPar={canEditPar}
            rowErrors={rowErrors}
            burnByProduct={data.burnByProduct}
            units4wByProduct={data.units4wByProduct}
            dismissedByProduct={data.dismissedByProduct}
          />
        );
      })}
    </InventoryShell>
  );
}

// ---- Shell with summary pills --------------------------------------

interface ShellProps {
  totals: Record<PillStatus, number>;
  children: ReactNode;
}

function InventoryShell({ totals, children }: ShellProps) {
  // 3B: surface a Reconciliation entry-point on Inventory, the
  // build-plan-designated home ("Inventory → Admin menu"). Link
  // is unconditionally rendered for anyone with view_history
  // (today any_member); the landing page decides whether to
  // show Start or Resume based on cycle state — InventoryShell
  // doesn't need to know.
  const session = useSession();
  const canSeeReconciliation = hasPermission(
    session,
    'reconciliation.view_history',
  );

  return (
    <div className="p-4 pb-6">
      <div className="grid grid-cols-3 gap-2 mb-4">
        {PILL_KEYS.map((key) => {
          const meta = PILL_META[key];
          return (
            <div
              key={key}
              className={`rounded-xl p-2.5 ${meta.bg} border ${meta.border}`}
            >
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide ${meta.text}`}
                >
                  {meta.label}
                </span>
              </div>
              <div
                className={`text-xl font-mono font-medium ${meta.text} mt-0.5 leading-none tabular-nums`}
              >
                {totals[key]}
              </div>
            </div>
          );
        })}
      </div>

      {canSeeReconciliation && (
        <Link
          to="/reconciliation"
          className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 mb-4 hover:border-slate-300 transition"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="rounded-lg bg-amber-50 p-1.5 flex-shrink-0">
              <Icon
                name="check"
                className="w-4 h-4 text-amber-700"
                strokeWidth={2}
              />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                Reconciliation
              </div>
              <div className="text-[11px] text-slate-500 leading-tight">
                Physical count of essentials
              </div>
            </div>
          </div>
          <Icon
            name="chevright"
            className="w-4 h-4 text-slate-400 flex-shrink-0"
          />
        </Link>
      )}

      {children}
    </div>
  );
}

// ---- Category section ----------------------------------------------

interface CategorySectionProps {
  category: Category;
  products: ProductRow[];
  isOpen: boolean;
  onToggle: () => void;
  adjustCount: (productId: string, delta: number) => Promise<void>;
  setPar: (
    productId: string,
    oldPar: number,
    newPar: number,
    reason: string,
  ) => Promise<void>;
  dismissSuggestion: (
    productId: string,
    dismissedForPar: number,
  ) => Promise<void>;
  canEditPar: boolean;
  rowErrors: Record<string, string>;
  burnByProduct: Map<string, number>;
  units4wByProduct: Map<string, number>;
  dismissedByProduct: Map<string, number>;
}

function CategorySection({
  category,
  products,
  isOpen,
  onToggle,
  adjustCount,
  setPar,
  dismissSuggestion,
  canEditPar,
  rowErrors,
  burnByProduct,
  units4wByProduct,
  dismissedByProduct,
}: CategorySectionProps) {
  const trackingLabel =
    category.tracking_mode === 'count' ? 'Count-based' : 'Two-bin Kanban';
  const badgeClass =
    CATEGORY_BADGE[category.code] ?? 'bg-slate-100 text-slate-700';

  return (
    <div className="mb-3 bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition"
      >
        <div className="flex items-center gap-2.5">
          <span
            className={`w-7 h-7 rounded-lg ${badgeClass} flex items-center justify-center text-xs font-bold`}
          >
            {category.code}
          </span>
          <div className="text-left">
            <div className="text-[14px] font-semibold text-slate-900">
              {category.name}
            </div>
            <div className="text-[11px] text-slate-500">
              {products.length} SKU{products.length === 1 ? '' : 's'} ·{' '}
              {trackingLabel}
            </div>
          </div>
        </div>
        <Icon
          name={isOpen ? 'chevdown' : 'chevright'}
          className="w-4 h-4 text-slate-400"
          strokeWidth={2.5}
        />
      </button>

      {isOpen && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {products.length === 0 ? (
            <div className="px-4 py-4 text-[11px] text-slate-400 italic">
              No products in this category.
            </div>
          ) : category.tracking_mode === 'count' ? (
            products.map((p) => (
              <CountRow
                key={p.id}
                product={p}
                onAdjust={(delta) => adjustCount(p.id, delta)}
                onSetPar={(oldPar, newPar, reason) =>
                  setPar(p.id, oldPar, newPar, reason)
                }
                onDismissSuggestion={(dismissedForPar) =>
                  dismissSuggestion(p.id, dismissedForPar)
                }
                canEditPar={canEditPar}
                error={rowErrors[p.id]}
                monthlyRate={burnByProduct.get(p.id) ?? null}
                units4w={units4wByProduct.get(p.id) ?? null}
                dismissedForPar={dismissedByProduct.get(p.id) ?? null}
              />
            ))
          ) : (
            products.map((p) => (
              <KanbanRow
                key={p.id}
                product={p}
                monthlyRate={burnByProduct.get(p.id) ?? null}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---- Rows ----------------------------------------------------------
//
// 2A.3 makes count rows interactive: non-dead products in count
// categories get a +/- stepper backed by `adjustCount`. Dead
// products keep the static (muted) count display from 2A.1 —
// dead inventory is by definition out of working stock, so the
// stepper would invite accidental edits with no real use case.
// Kanban rows remain non-interactive; their tap-to-log
// affordance is not yet ticketed.
//
// 4A.3a adds an inline par editor on CountRow. The "par N" text
// in the meta line becomes a tap target when:
//   - caller holds `par.edit` (canEditPar prop),
//   - product.par is not null (kanban products excluded),
//   - product is not dead.
// On tap, an inline editor expands below the main row content
// with a stepper for the new par, an optional reason text
// input, and Save/Cancel buttons.
//
// 4A.3 layers the par auto-suggestion on top: when burn rate
// implies a meaningfully different par (see computeParSuggestion
// in src/lib/inventory.ts for the gating rules), a small amber
// "suggested 6" pill renders inline in the meta line, with a
// separate × dismiss button. Tap on the suggestion opens the
// 4A.3a editor pre-filled to the suggested value; dismiss is
// per-user, persisted under RLS in par_suggestion_dismissals.
//
// The hint surfaces regardless of permission (all roles see the
// suggestion); only the editor itself stays gated on `par.edit`.
// Staff dismissing a suggestion they can't act on is fine — the
// dismissal is personal and doesn't affect managers' surface.

interface CountRowProps {
  product: ProductRow;
  onAdjust: (delta: number) => Promise<void>;
  /** 4A.3a — invoked when the user confirms a par change.
   *  Resolves on success; throws on failure (editor stays open
   *  so the user can retry or cancel). Page-level state
   *  manages the optimistic update + error toast + rollback. */
  onSetPar: (
    oldPar: number,
    newPar: number,
    reason: string,
  ) => Promise<void>;
  /** 4A.3 — invoked when the user taps the × on a par
   *  suggestion pill. dismissedForPar is the products.par value
   *  at dismissal time; suppression valid only while
   *  products.par == dismissed_for_par. */
  onDismissSuggestion: (dismissedForPar: number) => Promise<void>;
  /** True iff the caller holds `par.edit`. Server-side gate
   *  is in the RPC; this is for UI affordance only. */
  canEditPar: boolean;
  /** Inline error message shown briefly under the row when the
   *  most recent adjustCount or setPar on this product failed. */
  error: string | undefined;
  /**
   * Monthly burn rate from `product_burn_rates`. `null` when
   * the product has no qualifying consumption in the 4-week
   * window — the row hides the burn / DoS labels. Replaces
   * the prior 2A.5 unitsConsumed indirection (4A.1 swap).
   */
  monthlyRate: number | null;
  /** 4A.3 — 4-week consumption units from product_burn_rates,
   *  used as the confidence floor for the par suggestion. */
  units4w: number | null;
  /** 4A.3 — current dismissed_for_par for (user, product), or
   *  null if no dismissal record exists. */
  dismissedForPar: number | null;
}

function CountRow({
  product,
  onAdjust,
  onSetPar,
  onDismissSuggestion,
  canEditPar,
  error,
  monthlyRate: rawMonthlyRate,
  units4w,
  dismissedForPar,
}: CountRowProps) {
  const status = computeProductStatus(product);
  const isDead = status === 'dead';
  // Dead products: never show burn / DoS labels regardless of
  // computed values. Dead inventory is out of working stock by
  // definition; surfacing a "rate" or "supply" reading would
  // mislead.
  const monthlyRate = isDead ? null : rawMonthlyRate;
  const dos = isDead
    ? null
    : computeDaysOfSupply(product.count, monthlyRate);

  // 4A.3a — par editor is rendered only when all three hold:
  // permission, applicable (non-null par), live product.
  const parEditable =
    canEditPar && product.par !== null && !isDead;

  // 4A.3a — editor open state lives here. 4A.3 widens this from
  // `boolean` to `{ initialPar: number } | null` so the
  // suggestion pill can route in pre-filled to a non-current
  // value. ParLabelButton passes initialPar=product.par; the
  // suggestion pill passes initialPar=suggestedPar. Sibling
  // affordances, same open-the-editor call shape.
  const [editorOpen, setEditorOpen] = useState<
    { initialPar: number } | null
  >(null);

  // 4A.3 — compute par suggestion for this row. Dead products
  // suppress (rawMonthlyRate would be nulled above, but the
  // helper also guards against null current par). The hint
  // hides while the editor is open to avoid visual noise during
  // an active edit.
  const suggestedPar = isDead
    ? null
    : computeParSuggestion({
        currentPar: product.par,
        monthlyRate: rawMonthlyRate,
        units4w,
        dismissedForPar,
      });
  const showSuggestion = suggestedPar !== null && editorOpen === null;

  return (
    <div className={isDead ? 'bg-slate-50/60' : ''}>
      <div className="px-4 py-3 flex items-center gap-3">
        <div className={`w-1 h-10 rounded-full ${STATUS_DOT[status]}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div
              className={`text-[14px] font-medium truncate ${isDead ? 'text-slate-600' : 'text-slate-900'}`}
            >
              {product.name}
            </div>
            {product.essential && !isDead && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 bg-action-weak text-action rounded-full uppercase tracking-wide shrink-0">
                Essential
              </span>
            )}
            {isDead && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded-full uppercase tracking-wide shrink-0">
                Dead
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {product.uom}
            {product.par !== null && (
              <>
                {' '}
                ·{' '}
                {parEditable ? (
                  <ParLabelButton
                    par={product.par}
                    onOpen={() =>
                      setEditorOpen({ initialPar: product.par as number })
                    }
                  />
                ) : (
                  <>par {product.par}</>
                )}
              </>
            )}
            {monthlyRate !== null && <> · {formatBurnRate(monthlyRate)}</>}
            {dos !== null && (
              <>
                {' '}
                ·{' '}
                <span
                  className={
                    dos < DOS_AMBER_THRESHOLD_DAYS
                      ? 'text-gold font-medium'
                      : ''
                  }
                >
                  {dos}d supply
                </span>
              </>
            )}
            {showSuggestion && suggestedPar !== null && product.par !== null && (
              <ParSuggestionPill
                currentPar={product.par}
                suggestedPar={suggestedPar}
                canEditPar={parEditable}
                onOpen={() =>
                  setEditorOpen({ initialPar: suggestedPar })
                }
                onDismiss={() => {
                  void onDismissSuggestion(product.par as number);
                }}
              />
            )}
          </div>
        </div>
        {isDead ? (
          <div
            className="shrink-0 text-[15px] font-semibold tabular-nums min-w-[2ch] text-right text-slate-400"
            aria-label={`Count: ${product.count}`}
          >
            {product.count}
          </div>
        ) : (
          <Stepper count={product.count} onAdjust={onAdjust} />
        )}
      </div>
      {parEditable && product.par !== null && editorOpen !== null && (
        <ParEditorRegion
          currentPar={product.par}
          initialPendingPar={editorOpen.initialPar}
          onSave={onSetPar}
          onClose={() => setEditorOpen(null)}
        />
      )}
      {error !== undefined && (
        <div
          className="px-4 pb-2.5 -mt-1 text-[11px] text-red-600 flex items-center gap-1.5"
          role="alert"
          aria-live="polite"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ---- Par editor (4A.3a) --------------------------------------------
//
// Two-piece collaboration both rendered as siblings inside
// CountRow. CountRow owns the open/closed state; ParLabelButton
// fires `onOpen` to flip it; ParEditorRegion fires `onClose` to
// flip it back. Optimistic save + rollback are page-level (via
// onSetPar threaded through props).
//
// 4A.3 widens the open-state shape from `boolean` to
// `{ initialPar: number } | null`, which lets the suggestion
// pill route into the editor pre-filled to a non-current value.
// ParLabelButton still passes initialPar=currentPar; the
// suggestion pill passes initialPar=suggestedPar.

interface ParLabelButtonProps {
  par: number;
  onOpen: () => void;
}

function ParLabelButton({ par, onOpen }: ParLabelButtonProps) {
  return (
    <button
      type="button"
      className="underline decoration-dotted decoration-slate-400 underline-offset-2 hover:text-slate-700 active:text-slate-900 transition"
      aria-label={`Edit par level (currently ${par})`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
    >
      par {par}
    </button>
  );
}

interface ParEditorRegionProps {
  currentPar: number;
  /** 4A.3 — optional initial value for the pendingPar state.
   *  Defaults to currentPar when omitted (the 4A.3a behavior).
   *  Set to the suggested-par value when the editor is opened
   *  via the suggestion pill. */
  initialPendingPar?: number;
  onSave: (
    oldPar: number,
    newPar: number,
    reason: string,
  ) => Promise<void>;
  onClose: () => void;
}

function ParEditorRegion({
  currentPar,
  initialPendingPar,
  onSave,
  onClose,
}: ParEditorRegionProps) {
  const [pendingPar, setPendingPar] = useState<number>(
    initialPendingPar ?? currentPar,
  );
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Re-sync pending value if the underlying par changes (e.g.
  // another tab edited the same product) while the editor is
  // mounted. The user's in-progress value is overwritten in
  // that case — acceptable, the rare concurrent-edit case
  // shouldn't strand the user with a value referring to a par
  // that no longer exists.
  //
  // 4A.3 — track previous currentPar via ref so the effect only
  // fires on genuine prop change, not on first mount. StrictMode
  // double-invokes effects in dev, so the prior "skip first run"
  // ref pattern fired on the second invocation and clobbered
  // initialPendingPar. This pattern is StrictMode-safe because
  // the equality check stays true across both invocations of the
  // initial fire.
  const prevCurrentPar = useRef(currentPar);
  useEffect(() => {
    if (prevCurrentPar.current !== currentPar) {
      setPendingPar(currentPar);
      prevCurrentPar.current = currentPar;
    }
  }, [currentPar]);

  const isDirty = pendingPar !== currentPar;

  const handleSave = async (): Promise<void> => {
    if (!isDirty) {
      // No change — just close. The RPC's no-op short-circuit
      // would handle it gracefully but skipping the round-trip
      // is faster.
      onClose();
      return;
    }
    if (pendingPar < 0) return; // shouldn't reach; UI caps at 0

    setSaving(true);
    try {
      await onSave(currentPar, pendingPar, reason);
      onClose();
    } catch {
      // Page-level surfaces the error via rowErrors. Keep the
      // editor open so the user can retry or cancel; the
      // optimistic local par was rolled back by the page, so
      // the next render's currentPar will be the original — the
      // useEffect above re-syncs pendingPar accordingly.
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (): void => {
    onClose();
  };

  return (
    <div className="px-4 pb-3 -mt-1">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Par
          </span>
          <div className="flex items-center gap-1.5 bg-white rounded-lg p-0.5 border border-slate-200">
            <button
              type="button"
              onClick={() => setPendingPar((p) => Math.max(0, p - 1))}
              disabled={pendingPar <= 0 || saving}
              aria-label="Decrease par"
              className="w-7 h-7 rounded-md text-slate-700 flex items-center justify-center active:scale-95 transition disabled:opacity-40 disabled:active:scale-100 disabled:cursor-not-allowed"
            >
              <Icon name="minus" className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            <div
              className="w-8 text-center text-[15px] font-semibold text-slate-900 tabular-nums"
              aria-live="polite"
              aria-atomic="true"
            >
              {pendingPar}
            </div>
            <button
              type="button"
              onClick={() => setPendingPar((p) => p + 1)}
              disabled={saving}
              aria-label="Increase par"
              className="w-7 h-7 rounded-md text-slate-700 flex items-center justify-center active:scale-95 transition disabled:opacity-40"
            >
              <Icon name="plus" className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>
          {isDirty && (
            <span className="text-[11px] text-slate-500 tabular-nums">
              {currentPar} → {pendingPar}
            </span>
          )}
        </div>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={saving}
          placeholder="Why? (optional)"
          maxLength={200}
          className="mt-2.5 w-full px-2.5 py-1.5 text-[12px] bg-white border border-slate-200 rounded-md text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-action focus:ring-1 focus:ring-action/20 disabled:opacity-50"
        />
        <div className="flex items-center justify-end gap-2 mt-2.5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="h-8 px-3 rounded-md text-[12px] font-medium text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSave();
            }}
            disabled={saving || !isDirty}
            className="h-8 px-3 rounded-md text-[12px] font-semibold bg-action text-white hover:bg-action-hover active:bg-action-active transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving\u2026' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Par suggestion pill (4A.3) ------------------------------------
//
// Inline amber pill rendered in the row meta line when
// computeParSuggestion returns a non-null target. Two affordances:
//
//   - Tap the "suggested N" text → opens the 4A.3a editor
//     pre-filled to the suggested value. Only enabled when the
//     caller holds par.edit (the hint still surfaces to everyone,
//     but the tap-to-act behavior gates on the editor's
//     prerequisites — otherwise the tap would do nothing).
//
//   - Tap the × → dismisses the suggestion. Dismissal is keyed
//     to the *current* par value (par-value invalidation, Fork 2
//     option D); any subsequent par change invalidates the
//     dismissal and the suggestion can re-fire.
//
// Visual: amber background, small chevron implied by the
// "suggested N" framing. Matches the meta line's typographic
// scale (text-[11px]) so it doesn't visually compete with the
// burn / DoS labels.

interface ParSuggestionPillProps {
  currentPar: number;
  suggestedPar: number;
  canEditPar: boolean;
  onOpen: () => void;
  onDismiss: () => void;
}

function ParSuggestionPill({
  currentPar,
  suggestedPar,
  canEditPar,
  onOpen,
  onDismiss,
}: ParSuggestionPillProps) {
  const direction = suggestedPar > currentPar ? 'up' : 'down';
  const arrow = direction === 'up' ? '\u2191' : '\u2193';
  const verb = direction === 'up' ? 'Bump' : 'Lower';
  const ariaLabel = `${verb} par from ${currentPar} to ${suggestedPar}`;

  return (
    <>
      {' '}
      &middot;{' '}
      <span className="inline-flex items-center gap-1 align-baseline">
        {canEditPar ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            aria-label={ariaLabel}
            className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-200 active:bg-amber-300 transition"
          >
            <span aria-hidden="true">{arrow}</span>
            <span>par {suggestedPar}?</span>
          </button>
        ) : (
          <span
            aria-label={`Suggested par ${suggestedPar} (read-only — manager permission required to edit)`}
            className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
          >
            <span aria-hidden="true">{arrow}</span>
            <span>par {suggestedPar}?</span>
          </span>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="Dismiss par suggestion"
          className="inline-flex w-4 h-4 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 active:bg-slate-200 transition text-[12px] leading-none"
        >
          &times;
        </button>
      </span>
    </>
  );
}

interface StepperProps {
  count: number;
  onAdjust: (delta: number) => Promise<void>;
}

function Stepper({ count, onAdjust }: StepperProps) {
  // Adjust returns a Promise so callers can await — but for the
  // button click we fire-and-forget; rollback + error display
  // is handled by the page's adjustCount itself. Voiding here
  // satisfies the no-floating-promises lint without adding a
  // useless async wrapper.
  return (
    <div className="flex items-center gap-1.5 bg-slate-100 rounded-lg p-0.5 shrink-0">
      <button
        type="button"
        onClick={() => {
          void onAdjust(-1);
        }}
        disabled={count <= 0}
        aria-label="Decrease count"
        className="w-7 h-7 rounded-md bg-white border border-slate-200 text-slate-700 flex items-center justify-center active:scale-95 transition disabled:opacity-40 disabled:active:scale-100 disabled:cursor-not-allowed"
      >
        <Icon name="minus" className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      <div
        className="w-7 text-center text-[15px] font-semibold text-slate-900 tabular-nums"
        aria-live="polite"
        aria-atomic="true"
      >
        {count}
      </div>
      <button
        type="button"
        onClick={() => {
          void onAdjust(1);
        }}
        aria-label="Increase count"
        className="w-7 h-7 rounded-md bg-white border border-slate-200 text-slate-700 flex items-center justify-center active:scale-95 transition"
      >
        <Icon name="plus" className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}

interface KanbanRowProps {
  product: ProductRow;
  /**
   * Monthly burn rate from `product_burn_rates`. `null` when
   * the product has no qualifying consumption in the 4-week
   * window. Replaces the prior 2A.5 unitsConsumed indirection
   * (4A.1 swap).
   */
  monthlyRate: number | null;
}

function KanbanRow({ product, monthlyRate: rawMonthlyRate }: KanbanRowProps) {
  const status = computeProductStatus(product);
  const isDead = status === 'dead';
  // Kanban rows don't show DoS — no on-hand count, the
  // metric is meaningless. Burn rate is still informative
  // (consumption rate of the bin contents over time).
  const monthlyRate = isDead ? null : rawMonthlyRate;
  return (
    <div
      className={`px-4 py-3 flex items-center gap-3 ${isDead ? 'bg-slate-50/60' : ''}`}
    >
      <div className={`w-1 h-10 rounded-full ${STATUS_DOT[status]}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div
            className={`text-[14px] font-medium truncate ${isDead ? 'text-slate-600' : 'text-slate-900'}`}
          >
            {product.name}
          </div>
          {product.essential && !isDead && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 bg-action-weak text-action rounded-full uppercase tracking-wide shrink-0">
              Essential
            </span>
          )}
          {isDead && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded-full uppercase tracking-wide shrink-0">
              Dead
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {product.uom}
          {monthlyRate !== null && <> · {formatBurnRate(monthlyRate)}</>}
        </div>
      </div>
    </div>
  );
}

// ---- Sub-states ----------------------------------------------------

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="bg-white border border-amber-200 rounded-2xl p-5 flex flex-col items-center text-center">
      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-3">
        <Icon
          name="shield"
          className="w-5 h-5 text-amber-600"
          strokeWidth={2}
        />
      </div>
      <div className="text-sm font-semibold text-slate-900">
        Couldn't load inventory
      </div>
      <div className="text-xs text-slate-500 mt-1 leading-relaxed max-w-[260px]">
        Check your connection and retry. Refreshing the page also fixes most
        network blips.
      </div>
      <div className="text-[10px] text-slate-400 mt-2 font-mono break-words max-w-full">
        {message}
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 h-11 px-5 rounded-xl bg-action hover:bg-action-hover active:bg-action-active transition text-sm font-semibold text-white"
      >
        Retry
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center text-center">
      <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
        <Icon
          name="inventory"
          className="w-6 h-6 text-slate-400"
          strokeWidth={1.75}
        />
      </div>
      <div className="text-sm font-semibold text-slate-900">
        No products yet
      </div>
      <div className="text-xs text-slate-500 mt-1 leading-relaxed max-w-[260px]">
        Your clinic catalog is empty. Once products are added during onboarding,
        they'll show up here.
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="w-7 h-7 border-[3px] border-slate-200 border-t-action rounded-full animate-spin"
      role="status"
      aria-label="Loading inventory"
    />
  );
}